import type {Selection as BetSelection, SelectionGroup} from '@/components/betbuilder/types/betbuilder'
import {LogoData} from '@/types/client'
import {useRouter} from 'vue-router'
import {Store} from 'vuex'
import {useContextStore} from '@/store/modules/context'
import {getBaseUrl} from "@/utils/shareUrlUtils";

export function createShareService(store: Store<any>) {
	const router = useRouter()
	const contextStore = useContextStore()

	const getShareUrl = (): string => {
		const baseUrl = getBaseUrl()

		const hash = window.location.hash || ''
		const [hashPath, hashQuery = ''] = hash.split('?')
		// const params = new URLSearchParams(hashQuery)
		const params = new URLSearchParams()
		
		const isDirty = store.getters['betBuilderModule/isBetBuilderResponseDirty']
		const query = isDirty ? '' : (store.getters['betBuilderModule/lastQuery'] || '')
		const state = {
			bbLegSelectors: store.getters['betBuilderModule/betBuilderCriteria']?.bbLegSelectors
		}
		const stateStr = JSON.stringify(state)
		const combinedQuery = `${query}@@${stateStr}`
		
		params.set('query', combinedQuery)

		return `${baseUrl}${hashPath}?${params.toString()}`
	}

	const getShareData = () => {
		const legs: { betslipSelectionGroup: SelectionGroup }[] =
				store.getters['betBuilderModule/legs'] || []
		const betslipPrice = store.getters['betBuilderModule/betslipPriceDecimal']

		let numberOfSelections = 0
		legs.forEach((leg) => {
			numberOfSelections += leg.betslipSelectionGroup.selections?.length ?? 0
		})

		const events = legs.map((leg) => {
			const group = leg.betslipSelectionGroup

			const selections = Array.isArray(group.selections)
					? group.selections.map((sel: BetSelection) => {
						const outcome = sel.outcome?.description ?? 'Unknown outcome'
						const bookLabel =
								sel.book?.description || sel.book?.bookieId || 'Unknown book'
						const price =
								store.getters['betBuilderModule/selectionPrice'](
										sel.outcome?.bookieId
								) ?? 'N/A'
						return {outcome, bookLabel, price}
					})
					: []

			return {
				eventDescription: group.event?.description ?? 'Unknown event',
				selections,
			}
		})

		return {
			selectionCount: numberOfSelections,
			totalPrice: betslipPrice,
			events,
		}
	}

	const getLatestDate = (): string | undefined => {
		const legs = store.getters['betBuilderModule/legs'] || []

		const dateTimes = legs
				.map((leg: any) => leg?.betslipSelectionGroup?.event?.startDateTime?.dateTime)
				.filter((dt: string | undefined): dt is string => !!dt)

		if (!dateTimes.length) return undefined

		const furthest = dateTimes.reduce((latest: string | number | Date, current: string | number | Date) =>
				new Date(current) > new Date(latest) ? current : latest
		)

		return furthest
	}

	const LogoMap = store.getters['configModule/themes']

	const getLogoData = (opts?: { brand?: string }): LogoData | null => {
		let provider: string

  if (opts?.brand) {
    provider = opts.brand.toLowerCase()
  } else {
		if( Object.entries(contextStore.contextMap).length == 0 ) {
			console.log("contextMap is not initialised")
			return null
		}
    const path = router.currentRoute.value.path
    const match = path.match(/^\/(bet|odds|betpic)\/([^/]+)/)
    const lastSegment = match ? match[2].split('?')[0] : ''

			if (path.includes('/odds')) {
				provider = 'askodds'
			} else {
				const contextInfo = contextStore.contextMap[lastSegment]
				const brand = contextInfo?.brand
				provider = brand ? brand.toLowerCase() : (path.includes('/betpic') ? 'betpic' : 'askbookmaker')
			}
		}

		return LogoMap[provider] || LogoMap['askodds']
	}

	return {
		getShareUrl,
		getShareData,
		getLogoData,
		getLatestDate,
	}
}